import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { Observable, throwError } from 'rxjs';

import { AlertService } from './alert.service';
import { LoggerService } from './logger.service';
import { ConnectionStatusService } from './connection-status.service';

// based on this example: https://stackblitz.com/angular/xljvjlylmrp?file=src%2Fapp%2Fhttp-error-handler.service.ts

/** Type of the handleError function returned by HttpErrorHandler.createHandleError */
export type HandleError =
  (operation?: string) => (error: HttpErrorResponse) => Observable<never>;

@Injectable({
  providedIn: 'root'
})
export class HttpErrorHandlerService {

  constructor(
    private alertService: AlertService,
    private logger: LoggerService,
    private connectionStatus: ConnectionStatusService
  ) { }

  /** Create curried handleError function that already knows the service name */
  createHandleError = (serviceName = '') => {
    return (operation = 'operation') =>
      this.handleError(serviceName, operation);
  }

  /**
   * Returns a function that handles Http operation failures.
   * This error handler propagates errors correctly to allow proper error handling.
   * @param serviceName = name of the data service that attempted the operation
   * @param operation - name of the operation that failed
   */
  handleError(serviceName = '', operation = 'operation') {
    return (error: HttpErrorResponse): Observable<never> => {
      // TODO: send the error to remote logging infrastructure
      this.logger.error(error); // Logged only in development

      let message: string;
      let isConnectionError = false;

      // Check if it's a network/connection error
      if (error.status === 0 || error.status === undefined) {
        isConnectionError = true;
        message = 'Server non raggiungibile. Verifica la connessione di rete.';
        this.connectionStatus.markServerUnreachable();
      }
      // Check if it's a client-side error
      else if (error.error instanceof ErrorEvent) {
        message = error.error.message;
      }
      // Check if it's a server error with response body
      else if (error.status >= 400) {
        const errorBody = error.error ?
          (typeof error.error === 'string' ? error.error : JSON.stringify(error.error)) :
          'Nessun dettaglio disponibile';
        message = `Errore del server (${error.status}): ${errorBody}`;
      }
      // Fallback for other errors
      else {
        message = `Errore imprevisto (status: ${error.status})`;
      }

      this.logger.error(`${serviceName}: ${operation} failed: ${message}`);

      // Only show individual alert for non-connection errors
      // Connection errors are handled by the persistent ConnectionStatusService alert
      if (!isConnectionError) {
        this.alertService.error('Operazione fallita: ' + message);
      }

      // Propagate error correctly to allow proper error handling
      return throwError(() => new Error(message));
    };
  }

}
